import React from "react";
import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import XGSIcon from "../../ui-components/icon/xgsIcon";
import XGSIcons from "../../ui-components/icon/xgsIcons";
import moment from "moment-timezone";
import dateFormats from "../../app/data/common/dateFormats";

const columnHelper = createColumnHelper<any>();

// Helper function to convert GMT/UTC date string to local time and format
const formatDateFromGMT = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  return moment.utc(dateString).local().format(dateFormats.uiDateTime);
};

// Helper function to get date in ordinal format from GMT
const getDateOrdinalFromGMT = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  return moment.utc(dateString).local().format(dateFormats.uiDateOrdinal);
};

// Helper function to get time from GMT
const getTimeFromGMT = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  return moment.utc(dateString).local().format(dateFormats.uiTimeShort);
};

export const getPickupApprovalColumns = (showBillingApproved: boolean = false): ColumnDef<any>[] => {
  const columns: ColumnDef<any>[] = [
  columnHelper.accessor("pickup.pickupNumber", {
    id: "pickup.pickupNumber",
    header: "Pickup #",
    size: 130,
    cell: (cellProps) => (
      <div className="blue-link">
        {cellProps.getValue()}
      </div>
    )
  }),
  columnHelper.accessor("pickup.completedTime", {
    id: "pickup.completedTime",
    header: "Picked up",
    size: 120,
    cell: (cellProps) => {
      const date = formatDateFromGMT(cellProps.getValue() as string);
      return (
        <>
          {date?.split(" ")[0]}&nbsp;{" "}
          <span style={{ color: "rgba(79, 95, 114, 0.72)" }}>
            {date?.split(" ")[1]} {date?.split(" ")[2]}
          </span>
        </>
      )
    }
  }),
  columnHelper.accessor("pickup.probillNumber", {
    id: "pickup.probillNumber",
    header: "Probill",
    size: 140,
    enableSorting: false,
    cell: (cellProps) => (
      <div>
        {cellProps.getValue()}
      </div>
    )
  }),
  columnHelper.accessor("pickup.bolNumber", {
    id: "pickup.bolNumber",
    header: "BOL",
    size: 140,
    enableSorting: false,
    cell: (cellProps) => (
      <div>
        {cellProps.getValue()}
      </div>
    )
  }),
  columnHelper.accessor("consignee.name", {
    id: "consignee.name",
    header: "Consignee",
    size: 140,
  }),
  columnHelper.accessor("consignee.address", {
    id: "consignee.address",
    header: "Consignee Address",
    size: 250,
    enableSorting: false,
    cell: (cellProps) => {
      const value = cellProps.getValue() as any;
      return <>{value ? `${value.address1}, ${value.city}, ${value.postalCode}` : ""}</>
    }
  }),
  columnHelper.accessor("shipper.name", {
    id: "shipper.name",
    header: "Shipper",
    size: 140
  }),
  columnHelper.accessor("shipper", {
    id: "shipper.address",
    header: "Shipper address",
    size: 250,
    enableSorting: false,
    cell: (cellProps) => {
      const value = cellProps.getValue() as any;
      return <>{value.address.address1}, {value.address.city}, {value.address.postalCode}</>
    }
  }),
  columnHelper.accessor("originServiceCenter", {
    id: "originServiceCenter",
    header: "Origin Terminal",
    size: 200,
    enableSorting: false,
    cell: (cellProps) => {
      const value = cellProps.getValue() as any;
      return value ? (
        <>
          {value?.airportCode} - {value?.terminalName?.toUpperCase()} <span style={{ color: "rgba(79, 95, 114, 0.72)" }}>{value?.terminalNumber}</span>
        </>
      ) : <></>
    }
  }),
  columnHelper.accessor("destinationServiceCenter", {
    id: "destinationServiceCenter",
    header: "Destination Terminal",
    size: 200,
    enableSorting: false,
    cell: (cellProps) => {
      const value = cellProps.getValue() as any;
      return value ? (
        <>
          {value?.airportCode} - {value?.terminalName?.toUpperCase()} <span style={{ color: "rgba(79, 95, 114, 0.72)" }}>{value?.terminalNumber}</span>
        </>
      ) : <></>
    }
  })
  ];

  if (showBillingApproved) {
    columns.push(
      columnHelper.display({
        id: "billingApproved",
        header: "Confirmed",
        size: 200,
        enableSorting: false,
        cell: (cellProps) => {
          const billingApprovedDate = cellProps.row.original.billingApprovedDate;
          const dateOrdinal = getDateOrdinalFromGMT(billingApprovedDate);
          const time = getTimeFromGMT(billingApprovedDate);
          return dateOrdinal ? (
            <div className="xgs-appointment-summary__appointment-cell">
              <div className="xgs-appointment-summary__appointment-cell__check">
                <XGSIcon
                  className="xgs-appointment-summary__appointment-cell__check__tick"
                  icon={XGSIcons.faCheck}
                />
              </div>
              {dateOrdinal} <br />
              {time}
            </div>
          ) : null;
        }
      })
    );
  }

  return columns;
}
